import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PriorityBadge } from "@/components/PriorityBadge";
import { KnowledgeBadge } from "@/components/KnowledgeBadge";
import {
  Microscope, ArrowLeft, Phone, Users, Image as ImageIcon,
  MapPin, Megaphone, Clock, FileText,
} from "lucide-react";

/**
 * MUKTI 2.0 — Investigate Deep Dive (/investigate?q=...).
 *
 * One-shot aggregate view surfaced by the "Investigate connections" CTA on
 * the universal-search result block. Panels:
 *   1. Query header with detected type + top-line counters
 *   2. Related leads (click-through)
 *   3. Co-occurring identifiers (phones / aliases / images / localities)
 *   4. Timeline strip of ad records (newest first)
 *   5. Records table (link to /ads/:id)
 */
export default function Investigate() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!q) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: d } = await api.get(`/investigate?q=${encodeURIComponent(q)}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Investigate failed");
    } finally {
      setLoading(false);
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  if (!q) {
    return (
      <div className="py-20 text-center text-slate-500 text-sm">
        Nothing to investigate. <Link className="underline" to="/">Go back home</Link>.
      </div>
    );
  }
  if (loading || !data) {
    return <div className="py-20 text-center text-slate-400 text-sm">Loading intelligence on {q}…</div>;
  }

  const k = data.knows || {};
  const conn = data.connections || {};
  const leads = data.existing_intelligence || [];

  return (
    <div data-testid="investigate-page" className="space-y-6 max-w-5xl">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
      </Link>

      <header className="border border-slate-200 rounded-lg bg-white p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400">
              Investigating · detected {data.detected_type}
            </p>
            <h1 className="font-display text-2xl font-semibold text-slate-900 mt-1">
              <Microscope className="inline h-6 w-6 mr-2 text-slate-700" />
              {q}
            </h1>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center">
            {[
              ["Ads", k.ads], ["Aliases", k.aliases], ["Images", k.images],
              ["Locations", k.locations], ["Phones", k.phones], ["Publications", k.publications],
            ].map(([lbl, v]) => (
              <div key={lbl} className="px-2">
                <div className="font-display text-xl font-semibold tabular-nums text-slate-900">{v ?? 0}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
        {data.stands_out?.length ? (
          <ul className="mt-4 pt-4 border-t border-slate-100 space-y-1" data-testid="investigate-stands-out">
            {data.stands_out.map((s) => (
              <li key={s} className="flex gap-2 text-sm text-slate-700">
                <span className="text-slate-300">•</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </header>

      {/* Related leads */}
      <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="investigate-related-leads">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Related leads <span className="text-slate-300">({leads.length})</span>
        </h2>
        {leads.length ? (
          <div className="space-y-2">
            {leads.map((L) => (
              <Link
                key={L.id}
                to={`/leads/${L.id}`}
                data-testid={`investigate-lead-${L.id}`}
                className="flex items-center gap-3 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50"
              >
                <PriorityBadge priority={L.priority} />
                <span className="flex-1 text-sm text-slate-900 truncate">{L.title}</span>
                <KnowledgeBadge kind="mukti_inference" compact />
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No lead has been generated for this identifier yet.</p>
        )}
      </section>

      {/* Co-occurring identifiers */}
      <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="investigate-connections">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Co-occurring identifiers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ConnPanel icon={Phone} label="Phones" rows={conn.phones} />
          <ConnPanel icon={Users} label="Aliases" rows={conn.aliases} />
          <ConnPanel icon={ImageIcon} label="Reused photos" rows={conn.images}
                     render={(r) => `photo ${r.value.slice(0, 10)}…`} />
          <ConnPanel icon={MapPin} label="Localities" rows={conn.localities} />
        </div>
      </section>

      {/* Timeline */}
      <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="investigate-timeline">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
          <Clock className="inline h-4 w-4 mr-1" />
          Timeline (last 30 events)
        </h2>
        {data.timeline?.length ? (
          <ol className="relative border-l-2 border-slate-200 pl-4 space-y-2">
            {data.timeline.map((e) => (
              <li key={e.ad_id} className="relative">
                <span className="absolute -left-[1.21rem] top-1.5 h-2 w-2 rounded-full bg-slate-400 ring-4 ring-white" />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-slate-500">{e.date}</span>
                  <Link to={`/ads/${e.ad_id}`} className="text-sm text-slate-900 truncate hover:underline">
                    {e.title}
                  </Link>
                  {e.locality ? <span className="text-[11px] text-slate-500">· {e.locality}</span> : null}
                  {e.source ? <span className="text-[11px] text-slate-500">· {e.source}</span> : null}
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-slate-500 italic">No timeline events.</p>
        )}
      </section>

      {/* Records table */}
      <section className="border border-slate-200 rounded-lg bg-white p-5" data-testid="investigate-records">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
          <FileText className="inline h-4 w-4 mr-1" />
          Source records <span className="text-slate-300">({data.ads?.length || 0})</span>
        </h2>
        {data.ads?.length ? (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Locality</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Knowledge</th>
                </tr>
              </thead>
              <tbody>
                {data.ads.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 pr-3 font-mono text-xs text-slate-500 whitespace-nowrap">
                      {(a.posted_at || "").slice(0, 10)}
                    </td>
                    <td className="py-2 pr-3 text-slate-900 max-w-[320px] truncate">
                      <Link to={`/ads/${a.id}`} className="hover:underline" data-testid={`investigate-record-${a.id}`}>
                        {a.title}
                      </Link>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 text-xs">{a.locality || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600 text-xs">{a.source || "—"}</td>
                    <td className="py-2 pr-3"><KnowledgeBadge kind="source_fact" compact /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">No source records.</p>
        )}
      </section>
    </div>
  );
}

function ConnPanel({ icon: Icon, label, rows = [], render }) {
  return (
    <div className="border border-slate-100 rounded-md p-3 bg-slate-50">
      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-semibold uppercase tracking-wider mb-2">
        <Icon className="h-3.5 w-3.5" />
        {label}
        <span className="text-slate-400 font-mono normal-case tracking-normal">({rows.length})</span>
      </div>
      {rows.length ? (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.value} className="flex items-center justify-between text-sm">
              <span className="font-mono text-slate-800 truncate">{render ? render(r) : r.value}</span>
              <span className="text-[11px] font-mono text-slate-400">×{r.count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-400 italic">None.</p>
      )}
    </div>
  );
}
